import React from 'react'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import ExpandyPanel from '~/lib/ui/ExpandyPanel'
import { useTemplate } from '~/lib/pdf'
import { ArrayFields } from './common'
import {
  EditorItemField,
  useEditorItem,
  EditorYearMonthField,
} from './EditorItemProvider'
import { useTranslation } from '~/hooks/useTranslation'

const EducationItem = () => {
  const { t } = useTranslation('EducationItem')
  const {
    index,
    activeIndex,
    setActiveIndex,
    item,
    setItem,
    updateItem,
    deleteItem,
    updateCollection,
  } = useEditorItem()

  const { editorFieldEnabled } = useTemplate()
  const educationCoursesEnabled = editorFieldEnabled('educationCourses')
  const educationGpaEnabled = editorFieldEnabled('educationGpa')
  const educationLocationEnabled = editorFieldEnabled('educationLocation')
  const educationTenureEnabled = editorFieldEnabled('educationTenure')

  const headingLabel =
    item.institution && item.studyType
      ? `${item.studyType} at ${item.institution}`
      : item.institution || t('new_education_history')

  const toggleExpanded = () => {
    if (activeIndex == index) {
      setActiveIndex(-1)
    } else {
      setActiveIndex(index)
    }
  }

  const addCourse = () => {
    setCourses([...(item?.courses || []), ''])
  }

  const setCourses = (courses) => {
    const newEducationItem = { ...item, courses }
    setItem(newEducationItem)
  }

  const deleteCourse = (courseIndex) => {
    const updatedCourses = item.courses.filter((_, i) => i !== courseIndex)
    updateCollection('courses', updatedCourses)
  }

  const expanded = activeIndex == index
  return (
    <div className="mb-4 flex flex-row items-center">
      <ExpandyPanel
        headingText={headingLabel}
        expanded={expanded}
        index={index}
        minHeight="min-h-auto"
        onClick={toggleExpanded}
      >
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
          <EditorItemField
            label={t('institution')}
            fieldName="institution"
            containerClassName="md:col-span-2"
          />
          <EditorItemField label={t('degree')} fieldName="studyType" />
          <EditorItemField label={t('area_of_study')} fieldName="area" />
          {educationLocationEnabled && (
            <EditorItemField label={t('location')} fieldName="location" />
          )}
          {educationGpaEnabled && (
            <EditorItemField label={t('gpa')} fieldName="score" />
          )}
          {educationTenureEnabled && (
            <>
              <EditorYearMonthField
                label={t('start_date')}
                fieldName="startDate"
              />
              <EditorYearMonthField label={t('end_date')} fieldName="endDate" />
            </>
          )}
        </div>
        {educationCoursesEnabled && (
          <div>
            <div className="flex flex-row gap-2 pt-4">
              <span className="font-bold">
                {t('coursework_and_accomplishments')}
              </span>
              <button
                className="flex flex-row items-center text-primary"
                onClick={addCourse}
              >
                {t('add')}
                <PlusIcon className="ml-1 inline w-4 stroke-2" />
              </button>
            </div>
            <div className="flex flex-col gap-2 pt-4">
              <ArrayFields
                items={item.courses || []}
                keyPrefix="course"
                onChange={setCourses}
                onBlur={updateItem}
                onDeleteClick={deleteCourse}
              />
            </div>
          </div>
        )}
      </ExpandyPanel>
      {!expanded && (
        <button
          onClick={deleteItem}
          className="max-w-min text-[#b9b9b9]"
          aria-label={t('remove_education')}
        >
          <TrashIcon className="ml-2 w-8" />
        </button>
      )}
    </div>
  )
}

export default EducationItem
